import { insertAtHead, removeAt, replaceAt } from '@/utils/arrayUtils'
import { getItemFromLocalStorage, setItemToLocalStorage } from '@/utils/storageUtils'

export interface IHistoryContainer<S> {
  bookmarks: S[]
  recents: S[]
}

export enum Category {
  FAVORITE = 'f',
  RECENTLY = 'r',
}

export interface IFoldSetting {
  [Category.FAVORITE]?: boolean
  [Category.RECENTLY]?: boolean
}

// 최근 이용 => 북마크
export const addRecentToBookMarks = <S>(
  histories: IHistoryContainer<S>,
  predicate: (item: S) => boolean,
  createNewItem: (item: S) => S,
): IHistoryContainer<S> => {
  const { bookmarks: prevBookmarks, recents: prevRecents } = histories

  return prevRecents.reduce(
    (result, item, index) => {
      if (predicate(item)) {
        const newItem = createNewItem(item)
        const bookmarks = insertAtHead(result.bookmarks, newItem)
        const recents = replaceAt(result.recents, newItem, index)

        return {
          bookmarks,
          recents,
        }
      }

      return result
    },
    {
      bookmarks: [...prevBookmarks],
      recents: [...prevRecents],
    },
  )
}

// 즐겨찾기 => 최근 이용
export const moveBookmarkToRecents = <S>(
  histories: IHistoryContainer<S>,
  predicate: (item: S) => boolean,
  createNewItem: (item: S) => S,
): IHistoryContainer<S> => {
  const { bookmarks: prevBookmarks, recents: prevRecents } = histories

  return prevBookmarks.reduce(
    (result, item, index) => {
      if (predicate(item)) {
        const bookmarks = removeAt(result.bookmarks, index)
        const newItem = createNewItem(item)

        const recentIndex = result.recents.findIndex(predicate)
        const isIndexFound = recentIndex >= 0
        const recents = isIndexFound
          ? replaceAt(result.recents, newItem, recentIndex)
          : insertAtHead(result.recents, newItem)

        return {
          bookmarks,
          recents,
        }
      }

      return result
    },
    {
      bookmarks: [...prevBookmarks],
      recents: [...prevRecents],
    },
  )
}

// 즐겨찾기 아이템 목록및 최근 이용 아이템 목록에서 특정 아이템 삭제
export const removeFromBookmarkAndRecents = <S>(
  histories: IHistoryContainer<S>,
  predicate: (item: S) => boolean,
): IHistoryContainer<S> => {
  const { bookmarks: prevBookmarks, recents: prevRecents } = histories

  return {
    bookmarks: prevBookmarks.filter(predicate),
    recents: prevRecents.filter(predicate),
  }
}

export function storeFoldSetting(storageKey: string, category: Category, fold: boolean) {
  setItemToLocalStorage<IFoldSetting>(storageKey, {
    ...getItemFromLocalStorage<IFoldSetting>(storageKey),
    [category]: fold,
  })
}
